import classNames from 'classnames';
import { BiSortDown, BiSortUp } from 'react-icons/bi';
import { RiArrowDownSFill } from 'react-icons/ri';

import { Menu } from '@headlessui/react';

import { combineStrings, isDefinedString } from '../../utils';
import { Button } from '../button';

export type DropDownSortDir = 'Asc' | 'Desc';

export interface DropDownSortEntry<TProperty = string, TOrder = DropDownSortDir> {
  property: TProperty | null;
  order: TOrder | null;
  label?: string;
}

interface DropDownSortProps<TProperty, TOrder> {
  columns: DropDownSortEntry<TProperty, TOrder>[];
  selected: DropDownSortEntry<TProperty, TOrder>;
  getSelectedLabel?: (label: string | undefined, selected: DropDownSortEntry<TProperty, TOrder>) => string;
  getOptionLabel?: (label: string | undefined, option: DropDownSortEntry<TProperty, TOrder>) => string;
  promptLabel?: string;
  ascLabel?: string;
  descLabel?: string;
  onSortChange?: (entry: DropDownSortEntry<TProperty, TOrder>) => void;
}

export const DropDownSort = <TProperty, TOrder extends DropDownSortDir>({
  columns,
  selected,
  onSortChange,
  getSelectedLabel,
  getOptionLabel,
  promptLabel = 'Sort by',
  ascLabel = 'ascending',
  descLabel = 'descending',
}: DropDownSortProps<TProperty, TOrder>) => {
  const sortHandler = (entry: DropDownSortEntry<TProperty, TOrder>) => {
    onSortChange && onSortChange(entry);
  };

  const formatSortLabel = (label: string | undefined, order: TOrder | null) =>
    combineStrings(' ', label, order === 'Asc' ? ascLabel : descLabel);

  const getLabel = (entry: DropDownSortEntry<TProperty, TOrder>) =>
    isDefinedString(entry?.label) ? entry?.label : columns.find((x) => x.property === entry.property)?.label;

  return (
    <Menu as="div" className="relative">
      {({ open }) => (
        <>
          <Menu.Button as="div">
            <Button
              size="custom"
              className={`px-3 py-2 w-60 flex justify-between ${open ? 'rounded-b-none' : 'rounded'}`}
              color="primary-dark"
            >
              <div className="flex flex-col items-start">
                <div className="text-[0.6rem] leading-none uppercase text-brand-primary">{promptLabel}</div>
                <div className="text-sm ">
                  {formatSortLabel(
                    getSelectedLabel ? getSelectedLabel(getLabel(selected), selected) : selected.label,
                    selected.order,
                  )}
                </div>
              </div>
              <div className="flex justify-end">
                <RiArrowDownSFill className="text-lg" />
              </div>
            </Button>
          </Menu.Button>
          <Menu.Items className="absolute z-10 origin-top-right rounded-b-md bg-white shadow-2xl ring-1 ring-black ring-opacity-5 focus:outline-none w-full">
            {columns.map((option, idx) => (
              <Menu.Item key={`${option.property}-${idx}`}>
                {() => (
                  <div
                    className={classNames(
                      'flex justify-between px-3 py-2 cursor-pointer items-center border-b border-grey-400 text-sm hover:bg-brand-primary hover:text-black',
                      option.order === selected.order &&
                        option.property === selected.property &&
                        'text-brand-primary font-medium',
                    )}
                    onClick={() => sortHandler(option)}
                  >
                    {formatSortLabel(
                      getOptionLabel ? getOptionLabel(getLabel(option), option) : option.label,
                      option.order,
                    )}
                    {option.order === 'Asc' ? <BiSortUp /> : <BiSortDown />}
                  </div>
                )}
              </Menu.Item>
            ))}
          </Menu.Items>
        </>
      )}
    </Menu>
  );
};
